/**
 * AdminProvisioningAudit.jsx — /admin/provisioning-audit
 * v39.0.0 · Trazabilidad de Autoaprovisionamiento OLT
 *
 * Dos vistas:
 *  1. Timeline por OT (input búsqueda) — cada intento como tarjeta con JSON expandible
 *  2. Vista general paginada — tabla filtrable (solo fallos, por endpoint, por OLT)
 */
import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Search, Loader2, ChevronDown, ChevronRight, CheckCircle2,
  XCircle, Clock, Server, Activity, RefreshCw, AlertTriangle,
  BarChart3, Calendar, Zap,
  Radar, Send, Wifi, Package, FileCheck, Trash2, MoreHorizontal, Flag, List,
} from "lucide-react";

// v101.107.0 · Vista agrupada por OT: una etapa = un icono (verde = último intento OK, rojo = falló)
const STEP_ICONS = {
  consulta: Search, discovery: Radar, olt: Server, odoo: Send, axxess: Wifi,
  devolucion: Package, cierre: FileCheck, cancelacion: Trash2, otros: MoreHorizontal,
};
function fmtShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-EC", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function StepIcon({ st }) {
  const Icon = STEP_ICONS[st.key] || MoreHorizontal;
  const ok = !!st.last_ok;
  const tip = `${st.label}\nÚltimo: ${ok ? "OK" : "FALLÓ"} · ${st.last_endpoint || ""}${st.last_http ? ` · HTTP ${st.last_http}` : ""} · ${fmtShort(st.last_at)}`
    + `\nIntentos: ${st.ok} OK · ${st.fail} con error${st.last_error && !ok ? `\nError: ${st.last_error}` : ""}`;
  return (
    <span title={tip} data-testid={`step-${st.key}`}
      className="relative inline-flex items-center justify-center w-7 h-7 rounded-lg"
      style={{ background: ok ? "#DCFCE7" : "#FEE2E2", border: `1px solid ${ok ? "#86EFAC" : "#FCA5A5"}` }}>
      <Icon size={14} style={{ color: ok ? "#15803D" : "#B91C1C" }} />
      {(st.ok + st.fail) > 1 && (
        <span className="absolute -top-1 -right-1 text-[8px] font-bold rounded-full px-1"
          style={{ background: "#fff", color: "#475569", border: "1px solid #CBD5E1" }}>{st.ok + st.fail}</span>
      )}
    </span>
  );
}

const ENDPOINT_LABELS = {
  discover: "Discover ONTs",
  "set-ont": "Configurar ONT",
  confirm: "Confirmar",
  execute: "Ejecutar Axxess",
  complete_cpe: "Completar CPE",
  "webhook-complete-cpe": "Webhook CPE",
  "nearby-boxes": "Cajas Cercanas",
  "box-ports": "Puertos de Caja",
  "resolve-olt": "Resolver OLT",
};
const ENDPOINT_COLORS = {
  discover: { bg: "#DBEAFE", fg: "#1E40AF" },
  "set-ont": { bg: "#FEE2E2", fg: "#991B1B" },
  confirm: { bg: "#D1FAE5", fg: "#065F46" },
  execute: { bg: "#FEF3C7", fg: "#92400E" },
  complete_cpe: { bg: "#E0E7FF", fg: "#3730A3" },
  "webhook-complete-cpe": { bg: "#FCE7F3", fg: "#9D174D" },
  "nearby-boxes": { bg: "#F0FDF4", fg: "#166534" },
  "box-ports": { bg: "#FFF7ED", fg: "#9A3412" },
  "resolve-olt": { bg: "#F5F3FF", fg: "#5B21B6" },
};

export default function AdminProvisioningAudit() {
  const [tab, setTab] = useState("grouped"); // "grouped" | "general" | "ot" | "pending_sync" | "kpis"
  // v101.107.0 · Vista agrupada por OT
  const [gItems, setGItems] = useState([]);
  const [gTotal, setGTotal] = useState(0);
  const [gPage, setGPage] = useState(0);
  const [gLoading, setGLoading] = useState(false);
  const [gSteps, setGSteps] = useState([]);
  const [gDays, setGDays] = useState(7);
  const [gOnlyFailed, setGOnlyFailed] = useState(false);
  const [gQ, setGQ] = useState("");
  const G_PAGE = 30;
  const [otSearch, setOtSearch] = useState("");
  const [otId, setOtId] = useState(null);
  const [otItems, setOtItems] = useState([]);
  const [otLoading, setOtLoading] = useState(false);

  // General view
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterSuccess, setFilterSuccess] = useState("all");
  const [filterEndpoint, setFilterEndpoint] = useState("");
  const [filterOltIp, setFilterOltIp] = useState("");
  const PAGE_SIZE = 30;

  // v91.5.0 · Pending sync view
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  // v92.5.1 · Pending V2 count & retry
  const [pendingV2Count, setPendingV2Count] = useState(0);
  const [retryingV2, setRetryingV2] = useState(false);
  const [retryingV2Id, setRetryingV2Id] = useState(null);

  const fetchPendingV2Count = async () => {
    try {
      const { data } = await api.get("/v1/provisioning/odoo-sync/pending-count");
      setPendingV2Count(data.count || 0);
    } catch {}
  };
  const retryAllV2 = async () => {
    setRetryingV2(true);
    try {
      const { data } = await api.post("/v1/provisioning/odoo-sync/retry-pending");
      if (data.skipped) { toast.info("V2 deshabilitado, nada que reenviar"); }
      else { toast.success(`Reenvío: ${data.ok} OK, ${data.error_negocio} errores, ${data.still_pending} aún pendientes`); }
      fetchPendingV2Count();
      fetchPendingSync();
    } catch (e) { toast.error(e.response?.data?.detail || "Error reenviando"); }
    finally { setRetryingV2(false); }
  };
  const retrySingleV2 = async (taskId) => {
    setRetryingV2Id(taskId);
    try {
      await api.post(`/v1/provisioning/odoo-sync/retry/${taskId}`);
      toast.success(`OT ${taskId} reenviada OK`);
      fetchPendingSync();
      fetchPendingV2Count();
    } catch (e) { toast.error(e.response?.data?.detail || "Error reenviando"); }
    finally { setRetryingV2Id(null); }
  };

  // F7 · KPIs view
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [kpiDateFrom, setKpiDateFrom] = useState("");
  const [kpiDateTo, setKpiDateTo] = useState("");
  const [kpiFlujo, setKpiFlujo] = useState("");

  const fetchGeneral = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: p * PAGE_SIZE };
      if (filterSuccess !== "all") params.success = filterSuccess === "true";
      if (filterEndpoint) params.endpoint = filterEndpoint;
      if (filterOltIp) params.olt_ip = filterOltIp;
      const { data } = await api.get("/v1/provisioning-audit", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (e) {
      toast.error("Error cargando auditoría");
    } finally {
      setLoading(false);
    }
  }, [filterSuccess, filterEndpoint, filterOltIp]);

  const fetchGrouped = useCallback(async (p = 0) => {
    setGLoading(true);
    try {
      const params = { limit: G_PAGE, offset: p * G_PAGE, days: gDays, only_failed: gOnlyFailed };
      if (gQ.trim()) params.q = gQ.trim();
      const { data } = await api.get("/v1/provisioning-audit/by-ot", { params });
      setGItems(data.items || []);
      setGTotal(data.total || 0);
      setGSteps(data.steps || []);
      setGPage(p);
    } catch (e) {
      toast.error("Error cargando la vista por OT");
    } finally { setGLoading(false); }
  }, [gDays, gOnlyFailed, gQ]);

  useEffect(() => {
    if (tab === "grouped") fetchGrouped(0);
    if (tab === "general") fetchGeneral(0);
    if (tab === "pending_sync") { fetchPendingSync(); fetchPendingV2Count(); }
    if (tab === "kpis") fetchKpis();
  }, [tab, fetchGeneral, fetchGrouped]); // eslint-disable-line

  const fetchPendingSync = async () => {
    setPendingLoading(true);
    try {
      const { data } = await api.get("/v1/provisioning/admin/pending-sync");
      setPendingItems(data.items || []);
    } catch {
      toast.error("Error cargando OTs pendientes de sync");
    } finally { setPendingLoading(false); }
  };

  const fetchKpis = async () => {
    setKpisLoading(true);
    try {
      const params = {};
      if (kpiDateFrom) params.date_from = kpiDateFrom;
      if (kpiDateTo) params.date_to = kpiDateTo;
      if (kpiFlujo) params.flujo = parseInt(kpiFlujo);
      const { data } = await api.get("/v1/provisioning/admin/kpis", { params });
      setKpis(data);
    } catch {
      toast.error("Error cargando KPIs");
    } finally { setKpisLoading(false); }
  };

  const handleRetrySync = async (taskId) => {
    setRetryingId(taskId);
    try {
      const { data } = await api.post(`/v1/provisioning/admin/retry-sync/${taskId}`);
      if (data.already_synced) {
        toast.success(`OT ${taskId} ya estaba sincronizada`);
      } else {
        toast.success(`Reintento disparado para OT ${taskId} (evento: ${data.evento_id?.slice(0, 8)}...)`);
      }
      // Refrescar después de 3s
      setTimeout(fetchPendingSync, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al reintentar sync");
    } finally { setRetryingId(null); }
  };

  const searchOT = async (value) => {
    const raw = String(typeof value === "string" ? value : otSearch).replace(/\D/g, "");
    if (!raw) return;
    setOtLoading(true);
    setOtId(null);
    try {
      const { data } = await api.get(`/v1/provisioning-audit/orders/${raw}`);
      setOtId(data.odoo_task_id);
      setOtItems(data.items || []);
      if (!data.items?.length) toast.info("Sin registros de aprovisionamiento para esta OT");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error buscando OT");
    } finally {
      setOtLoading(false);
    }
  };

  // v101.107.0 · Clic en la OT → sus logs (pestaña «Por OT» ya buscada)
  const openOtLogs = (num) => {
    const v = String(num || "");
    setTab("ot");
    setOtSearch(v);
    searchOT(v);
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="provisioning-audit-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2" style={{ color: "#0A3747" }}>
            <Activity size={20} className="text-cyan-500" />
            Auditoría de Aprovisionamiento
          </h1>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setTab("grouped")}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all ${tab === "grouped" ? "bg-[#0A3747] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid="tab-grouped"
            >
              <Activity size={11} className="inline mr-1" /> Por OT · etapas
            </button>
            <button
              onClick={() => setTab("general")}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all ${tab === "general" ? "bg-[#0A3747] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid="tab-general"
            >
              <List size={11} className="inline mr-1" /> Todos los registros
            </button>
            <button
              onClick={() => setTab("ot")}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all ${tab === "ot" ? "bg-[#0A3747] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid="tab-ot"
            >
              <Search size={11} className="inline mr-1" /> Por OT
            </button>
            <button
              onClick={() => setTab("pending_sync")}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all ${tab === "pending_sync" ? "bg-amber-600 text-white" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
              data-testid="tab-pending-sync"
            >
              <AlertTriangle size={11} className="inline mr-1" /> Sync Pendiente
            </button>
            <button
              onClick={() => setTab("kpis")}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all ${tab === "kpis" ? "bg-emerald-700 text-white" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
              data-testid="tab-kpis"
            >
              <BarChart3 size={11} className="inline mr-1" /> KPIs
            </button>
          </div>
        </div>

        {/* ═══ v101.107.0 · Tab: agrupado por OT con etapas ═══ */}
        {tab === "grouped" && (
          <div className="space-y-3" data-testid="grouped-panel">
            <div className="flex items-center gap-2 flex-wrap">
              <input value={gQ} onChange={(e) => setGQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchGrouped(0)}
                placeholder="OT o técnico" className="text-xs border rounded-lg px-2 py-1.5 w-44" data-testid="grouped-q" />
              <select value={gDays} onChange={(e) => setGDays(Number(e.target.value))} className="text-xs border rounded-lg px-2 py-1.5">
                <option value={1}>Últimas 24 h</option>
                <option value={3}>Últimos 3 días</option>
                <option value={7}>Últimos 7 días</option>
                <option value={30}>Últimos 30 días</option>
              </select>
              <label className="flex items-center gap-1 text-xs text-gray-700">
                <input type="checkbox" checked={gOnlyFailed} onChange={(e) => setGOnlyFailed(e.target.checked)} /> Solo con errores
              </label>
              <button onClick={() => fetchGrouped(0)} disabled={gLoading}
                className="px-3 py-1.5 text-[11px] font-bold bg-[#0A3747] text-white rounded-lg hover:bg-[#0C4A5E] disabled:opacity-50 flex items-center gap-1">
                {gLoading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Aplicar
              </button>
              <span className="text-[10px] text-gray-500 font-mono ml-auto">{gTotal} OT{gTotal !== 1 ? "s" : ""}</span>
            </div>
            {/* Leyenda */}
            <div className="flex items-center gap-3 flex-wrap text-[10px] text-gray-500">
              {gSteps.filter((x) => x.key !== "otros").map((x) => {
                const I = STEP_ICONS[x.key] || MoreHorizontal;
                return <span key={x.key} className="inline-flex items-center gap-1"><I size={11} /> {x.label}</span>;
              })}
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded" style={{ background: "#DCFCE7", border: "1px solid #86EFAC" }} /> último intento OK</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded" style={{ background: "#FEE2E2", border: "1px solid #FCA5A5" }} /> último intento con error</span>
            </div>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs" data-testid="grouped-table">
                <thead className="bg-[#0A3747] text-white text-[10px] uppercase font-mono tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">OT</th>
                    <th className="px-3 py-2 text-left">Actividad</th>
                    <th className="px-3 py-2 text-left">Etapas del proceso</th>
                    <th className="px-3 py-2 text-left">Se quedó en</th>
                    <th className="px-3 py-2 text-left">Último movimiento</th>
                    <th className="px-3 py-2 text-left">Técnico</th>
                    <th className="px-2 py-2 text-center" title="Tarea cerrada">Cierre</th>
                  </tr>
                </thead>
                <tbody>
                  {gLoading && gItems.length === 0 ? (
                    <tr><td colSpan={7} className="py-10 text-center"><Loader2 size={18} className="mx-auto animate-spin text-gray-400" /></td></tr>
                  ) : gItems.length === 0 ? (
                    <tr><td colSpan={7} className="py-10 text-center text-sm text-gray-400 italic">Sin registros</td></tr>
                  ) : gItems.map((it) => (
                    <tr key={it.odoo_task_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`grouped-row-${it.odoo_task_id}`}>
                      <td className="px-3 py-2 font-mono font-bold text-[#0A3747] whitespace-nowrap">
                        <button onClick={() => openOtLogs(it.ot_number)} className="hover:underline" title="Ver los logs de esta OT">
                          {it.ot_number}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-gray-600 max-w-[160px] truncate" title={it.activity_type || ""}>{it.activity_type || "—"}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {(it.steps || []).map((st) => <StepIcon key={st.key} st={st} />)}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {it.stuck_at
                          ? <span className="text-red-700 font-semibold">{it.stuck_at}</span>
                          : <span className="text-emerald-700">Sin errores pendientes</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-600 whitespace-nowrap">{fmtShort(it.last_at)}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[160px] truncate" title={(it.technicians || []).join(", ")}>
                        {(it.technicians || []).slice(-1)[0] || "—"}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span title={it.closed ? "Tarea cerrada" : `Tarea abierta${it.internal_status ? ` (${it.internal_status})` : ""}`}
                          className="inline-flex" data-testid={`flag-${it.closed ? "closed" : "open"}`}>
                          <Flag size={12} style={{ color: it.closed ? "#16A34A" : "#DC2626", opacity: 0.7 }} />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {gTotal > G_PAGE && (
              <div className="flex items-center justify-between px-1">
                <button onClick={() => fetchGrouped(gPage - 1)} disabled={gPage === 0 || gLoading}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-30">Anterior</button>
                <span className="text-xs font-mono text-gray-500">Pág {gPage + 1} / {Math.ceil(gTotal / G_PAGE)}</span>
                <button onClick={() => fetchGrouped(gPage + 1)} disabled={(gPage + 1) * G_PAGE >= gTotal || gLoading}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-30">Siguiente</button>
              </div>
            )}
          </div>
        )}

        {/* ═══ Tab: Por OT ═══ */}
        {tab === "ot" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={otSearch}
                onChange={(e) => setOtSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchOT(otSearch)}
                placeholder="Número de OT (ej: 00330799)"
                className="flex-1 max-w-xs text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                data-testid="input-ot-search"
              />
              <button
                onClick={() => searchOT(otSearch)}
                disabled={otLoading}
                className="px-4 py-2 text-sm font-bold bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="btn-search-ot"
              >
                {otLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                Buscar
              </button>
            </div>

            {otId && (
              <div>
                <div className="text-sm font-bold text-[#0A3747] mb-3" data-testid="ot-timeline-header">
                  Timeline OT {otId} — {otItems.length} registro{otItems.length !== 1 ? "s" : ""}
                </div>
                {otItems.length === 0 ? (
                  <div className="text-center py-10 text-sm text-gray-500 italic">
                    No hay intentos de aprovisionamiento registrados para esta OT.
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="ot-timeline">
                    {otItems.map((item, idx) => (
                      <AuditCard key={item.id} item={item} index={idx} total={otItems.length} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══ Tab: Vista General ═══ */}
        {tab === "general" && (
          <div className="space-y-3">
            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="general-filters">
              <select
                value={filterSuccess}
                onChange={(e) => setFilterSuccess(e.target.value)}
                className="text-xs border rounded-lg px-2 py-1.5"
                data-testid="filter-success"
              >
                <option value="all">Todos</option>
                <option value="false">Solo Fallos</option>
                <option value="true">Solo Exitosos</option>
              </select>
              <select
                value={filterEndpoint}
                onChange={(e) => setFilterEndpoint(e.target.value)}
                className="text-xs border rounded-lg px-2 py-1.5"
                data-testid="filter-endpoint"
              >
                <option value="">Todos los endpoints</option>
                <option value="discover">Discover</option>
                <option value="set-ont">Set-ONT</option>
                <option value="confirm">Confirm</option>
                <option value="execute">Execute</option>
              </select>
              <input
                type="text"
                value={filterOltIp}
                onChange={(e) => setFilterOltIp(e.target.value)}
                placeholder="Filtrar por IP de OLT"
                className="text-xs border rounded-lg px-2 py-1.5 w-40"
                data-testid="filter-olt-ip"
              />
              <button
                onClick={() => fetchGeneral(0)}
                disabled={loading}
                className="px-3 py-1.5 text-[11px] font-bold bg-[#0A3747] text-white rounded-lg hover:bg-[#0C4A5E] disabled:opacity-50 flex items-center gap-1"
                data-testid="btn-apply-filters"
              >
                {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                Aplicar
              </button>
              <span className="text-[10px] text-gray-500 font-mono ml-auto">
                {total} registro{total !== 1 ? "s" : ""}
              </span>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs" data-testid="audit-table">
                <thead className="bg-[#0A3747] text-white text-[10px] uppercase font-mono tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">Fecha</th>
                    <th className="px-3 py-2 text-left">OT</th>
                    <th className="px-3 py-2 text-left">Endpoint</th>
                    <th className="px-3 py-2 text-left">OLT IP</th>
                    <th className="px-3 py-2 text-center">Status</th>
                    <th className="px-3 py-2 text-right">Duración</th>
                    <th className="px-3 py-2 text-left">Técnico</th>
                    <th className="px-3 py-2 text-left">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && items.length === 0 ? (
                    <tr><td colSpan={8} className="py-10 text-center"><Loader2 size={18} className="mx-auto animate-spin text-gray-400" /></td></tr>
                  ) : items.length === 0 ? (
                    <tr><td colSpan={8} className="py-10 text-center text-sm text-gray-400 italic">Sin registros</td></tr>
                  ) : items.map((it) => (
                    <tr
                      key={it.id}
                      className={`border-t border-gray-100 hover:bg-gray-50 ${!it.success ? "bg-red-50/40" : ""}`}
                      data-testid={`audit-row-${it.id}`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-gray-600">
                        {fmtDate(it.created_at)}
                      </td>
                      <td className="px-3 py-2 font-mono font-bold text-[#0A3747] whitespace-nowrap">
                        <button
                          onClick={() => openOtLogs(it.ot_number || it.odoo_task_id)}
                          className="hover:underline"
                          title={`ID interno Odoo: ${it.odoo_task_id}`}
                        >
                          {it.ot_number || it.odoo_task_id}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <EndpointBadge endpoint={it.endpoint} />
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-600 whitespace-nowrap">
                        {it.olt_ip || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <StatusBadge success={it.success} httpStatus={it.http_status} errorMessage={it.error_message} />
                      </td>
                      <td className="px-3 py-2 text-right font-mono whitespace-nowrap">
                        {it.duration_ms != null ? `${it.duration_ms}ms` : "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap max-w-[120px] truncate" title={it.technician_email}>
                        {it.technician_email || "—"}
                      </td>
                      <td className="px-3 py-2 text-red-600 max-w-[200px] truncate" title={it.error_message}>
                        {it.error_message || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between px-1">
                <button
                  onClick={() => fetchGeneral(page - 1)}
                  disabled={page === 0 || loading}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-30"
                >
                  Anterior
                </button>
                <span className="text-xs font-mono text-gray-500">
                  Pág {page + 1} / {Math.ceil(total / PAGE_SIZE)}
                </span>
                <button
                  onClick={() => fetchGeneral(page + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= total || loading}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-30"
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        )}
        {/* ═══ Tab: Sync Pendiente ═══ */}
        {tab === "pending_sync" && (
          <div className="space-y-3" data-testid="pending-sync-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-sm font-bold text-amber-800">
                OTs con sincronización Odoo pendiente
              </span>
              <div className="flex items-center gap-2">
                {pendingV2Count > 0 && (
                  <button
                    onClick={retryAllV2}
                    disabled={retryingV2}
                    className="px-3 py-1.5 text-[11px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                    data-testid="btn-retry-all-v2"
                  >
                    {retryingV2 ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
                    Reenviar pendientes V2 ({pendingV2Count})
                  </button>
                )}
                <button
                  onClick={() => { fetchPendingSync(); fetchPendingV2Count(); }}
                  disabled={pendingLoading}
                  className="px-3 py-1.5 text-[11px] font-bold bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"
                >
                  {pendingLoading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                  Refrescar
                </button>
              </div>
            </div>

            {pendingLoading ? (
              <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-amber-500" /></div>
            ) : pendingItems.length === 0 ? (
              <div className="text-center py-10 text-sm text-gray-400 italic">
                No hay OTs pendientes de sincronización.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-amber-200">
                <table className="w-full text-xs" data-testid="pending-sync-table">
                  <thead className="bg-amber-600 text-white text-[10px] uppercase font-mono tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left">OT</th>
                      <th className="px-3 py-2 text-left">Cliente</th>
                      <th className="px-3 py-2 text-left">Suscripción</th>
                      <th className="px-3 py-2 text-left">Evento ID</th>
                      <th className="px-3 py-2 text-center">Intentos</th>
                      <th className="px-3 py-2 text-left">Último Error</th>
                      <th className="px-3 py-2 text-left">Último Intento</th>
                      <th className="px-3 py-2 text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingItems.map((it) => (
                      <tr key={it.odoo_task_id} className="border-t border-amber-100 hover:bg-amber-50/50" data-testid={`sync-row-${it.odoo_task_id}`}>
                        <td className="px-3 py-2 font-mono font-bold text-amber-900">{it.odoo_task_id}</td>
                        <td className="px-3 py-2 text-gray-700 max-w-[150px] truncate">{it.client_name || "—"}</td>
                        <td className="px-3 py-2 font-mono text-gray-600">{it.subscription_code || "—"}</td>
                        <td className="px-3 py-2 font-mono text-gray-500 text-[9px]">{(it.odoo_sync_evento_id || "—").slice(0, 12)}...</td>
                        <td className="px-3 py-2 text-center">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">
                            {it.attempts || 0}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-red-600 max-w-[200px] truncate text-[10px]" title={it.last_error}>
                          {it.last_error || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-500 text-[10px] font-mono whitespace-nowrap">
                          {it.last_attempt_at ? fmtDate(it.last_attempt_at) : "—"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <button
                              onClick={() => handleRetrySync(it.odoo_task_id)}
                              disabled={retryingId === it.odoo_task_id}
                              className="px-3 py-1.5 text-[10px] font-bold bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1 mx-auto"
                              data-testid={`btn-retry-sync-${it.odoo_task_id}`}
                            >
                              {retryingId === it.odoo_task_id ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                              Reintentar V1
                            </button>
                            <button
                              onClick={() => retrySingleV2(it.odoo_task_id)}
                              disabled={retryingV2Id === it.odoo_task_id}
                              className="px-3 py-1.5 text-[10px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1 mx-auto"
                              data-testid={`btn-retry-v2-${it.odoo_task_id}`}
                            >
                              {retryingV2Id === it.odoo_task_id ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
                              Reenviar V2
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ═══ Tab: KPIs (FASE 7) ═══ */}
        {tab === "kpis" && (
          <div className="space-y-4" data-testid="kpis-panel">
            {/* Filtros */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1">
                <Calendar size={13} className="text-gray-400" />
                <input type="date" value={kpiDateFrom} onChange={e => setKpiDateFrom(e.target.value)}
                  className="text-xs border rounded-lg px-2 py-1.5" data-testid="kpi-date-from" />
                <span className="text-[10px] text-gray-400">—</span>
                <input type="date" value={kpiDateTo} onChange={e => setKpiDateTo(e.target.value)}
                  className="text-xs border rounded-lg px-2 py-1.5" data-testid="kpi-date-to" />
              </div>
              <select value={kpiFlujo} onChange={e => setKpiFlujo(e.target.value)}
                className="text-xs border rounded-lg px-2 py-1.5" data-testid="kpi-flujo-filter">
                <option value="">Todos los flujos</option>
                <option value="1">F1 · Instalación FO</option>
                <option value="2">F2 · Radio Enlace</option>
                <option value="3">F3 · Cambio Equipo</option>
                <option value="4">F4 · Traslado</option>
                <option value="5">F5 · Soporte FO</option>
                <option value="6">F6 · Soporte RE</option>
              </select>
              <button onClick={fetchKpis} disabled={kpisLoading}
                className="px-3 py-1.5 text-[11px] font-bold bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 disabled:opacity-50 flex items-center gap-1"
                data-testid="btn-apply-kpis">
                {kpisLoading ? <Loader2 size={11} className="animate-spin" /> : <BarChart3 size={11} />}
                Calcular
              </button>
            </div>

            {kpisLoading ? (
              <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-emerald-500" /></div>
            ) : !kpis ? (
              <div className="text-center py-10 text-sm text-gray-400 italic">
                Haga clic en "Calcular" para generar KPIs.
              </div>
            ) : (
              <>
                {/* KPI Cards Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-cards">
                  <KpiCard label="Éxito 1er intento" value={`${kpis.pct_first_attempt_success}%`}
                    sub={`${kpis.first_attempt_sample} intentos`} color="#16A34A" />
                  <KpiCard label="Tiempo wizard (avg)" value={kpis.avg_wizard_seconds ? `${Math.round(kpis.avg_wizard_seconds)}s` : "N/A"}
                    sub={`${kpis.wizard_sample} sesiones`} color="#2563EB" />
                  <KpiCard label="Auth OLT 401/403" value={`${kpis.pct_olt_auth_fail}%`}
                    sub={`${kpis.olt_auth_sample} calls`} color={kpis.pct_olt_auth_fail > 5 ? "#DC2626" : "#059669"} />
                  <KpiCard label="complete_cpe OK" value={`${kpis.pct_complete_cpe_ok}%`}
                    sub={`${kpis.complete_cpe_sample} envíos`} color="#7C3AED" />
                  <KpiCard label="complete_task OK" value={`${kpis.pct_complete_task_ok}%`}
                    sub={`${kpis.complete_task_sample} envíos`} color="#0891B2" />
                  <KpiCard label="Pendiente Sync" value={kpis.pending_sync_count}
                    sub="OTs sin sincronizar" color={kpis.pending_sync_count > 0 ? "#D97706" : "#059669"} />
                  <KpiCard label="Visitas con excedente" value={`${kpis.pct_visits_with_excess}%`}
                    sub={`${kpis.excess_sample} verificaciones`} color="#EA580C" />
                </div>

                {/* Tickets por causa */}
                {kpis.tickets_by_cause?.length > 0 && (
                  <div className="rounded-xl border border-gray-200 overflow-hidden" data-testid="kpi-tickets-table">
                    <div className="px-3 py-2 bg-[#0A3747] text-white text-[10px] font-bold uppercase tracking-wider">
                      Tickets por Causa
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-[10px] text-gray-500 uppercase">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Causa</th>
                          <th className="px-3 py-1.5 text-center">Total</th>
                          <th className="px-3 py-1.5 text-center">Abiertos</th>
                          <th className="px-3 py-1.5 text-center">Resueltos</th>
                          <th className="px-3 py-1.5 text-right">Tiempo resolución (avg)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kpis.tickets_by_cause.map(tc => (
                          <tr key={tc.causa} className="border-t border-gray-100 hover:bg-gray-50">
                            <td className="px-3 py-1.5 font-mono font-bold text-[#0A3747]">{tc.causa?.replace(/_/g, " ")}</td>
                            <td className="px-3 py-1.5 text-center">{tc.total}</td>
                            <td className="px-3 py-1.5 text-center">
                              {tc.abiertos > 0 ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">{tc.abiertos}</span> : "0"}
                            </td>
                            <td className="px-3 py-1.5 text-center">
                              {tc.resueltos > 0 ? <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">{tc.resueltos}</span> : "0"}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono">
                              {tc.avg_resol_secs ? `${Math.round(tc.avg_resol_secs / 60)} min` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

/* ═══ Sub-components ═══ */

function KpiCard({ label, value, sub, color }) {
  return (
    <div className="rounded-xl p-3 border" style={{ borderColor: `${color}30`, background: `${color}08` }}
      data-testid={`kpi-${label.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}>
      <div className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>{label}</div>
      <div className="text-2xl font-black mt-1 font-mono" style={{ color }}>{value}</div>
      <div className="text-[10px] mt-0.5" style={{ color: "#9CA3AF" }}>{sub}</div>
    </div>
  );
}

function AuditCard({ item, index, total }) {
  const [expanded, setExpanded] = useState(false);
  const ec = ENDPOINT_COLORS[item.endpoint] || { bg: "#F3F4F6", fg: "#374151" };

  return (
    <div
      className={`relative pl-8 ${index < total - 1 ? "pb-3" : ""}`}
      data-testid={`audit-card-${item.id}`}
    >
      {/* Timeline line */}
      {index < total - 1 && (
        <div className="absolute left-[14px] top-8 bottom-0 w-[2px]" style={{ background: "#E2E8F0" }} />
      )}
      {/* Timeline dot */}
      <div
        className="absolute left-[6px] top-2 w-[18px] h-[18px] rounded-full flex items-center justify-center border-2"
        style={{
          borderColor: item.success ? "#059669" : "#DC2626",
          background: item.success ? "#D1FAE5" : "#FEE2E2",
        }}
      >
        {item.success
          ? <CheckCircle2 size={10} color="#059669" />
          : <XCircle size={10} color="#DC2626" />}
      </div>

      <div
        className={`rounded-xl border transition-all ${item.success ? "border-gray-200 bg-white" : isPendingSync(item.error_message) ? "border-amber-200 bg-amber-50/30" : "border-red-200 bg-red-50/30"}`}
      >
        {/* Header row */}
        <div
          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50/50 rounded-t-xl"
          onClick={() => setExpanded(!expanded)}
        >
          <EndpointBadge endpoint={item.endpoint} integrationName={item.integration_name} />
          <StatusBadge success={item.success} httpStatus={item.http_status} errorMessage={item.error_message} />
          {item.duration_ms != null && (
            <span className="text-[10px] font-mono text-gray-500 flex items-center gap-0.5">
              <Clock size={9} /> {item.duration_ms}ms
            </span>
          )}
          {item.olt_ip && (
            <span className="text-[10px] font-mono text-gray-500 flex items-center gap-0.5">
              <Server size={9} /> {item.olt_ip}
            </span>
          )}
          <span className="ml-auto text-[9px] font-mono text-gray-400">
            {fmtDate(item.created_at)}
          </span>
          {item.technician_email && (
            <span className="text-[9px] text-gray-400 truncate max-w-[100px]">{item.technician_email}</span>
          )}
          <span className="text-gray-400">
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </span>
        </div>

        {/* Error message */}
        {item.error_message && (
          <div className={`mx-3 mb-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border-l-3 ${isPendingSync(item.error_message) ? "bg-amber-100 text-amber-800 border-amber-500" : "bg-red-100 text-red-800 border-red-500"}`} data-testid="audit-error-msg">
            {item.error_message}
          </div>
        )}

        {/* Expandable JSON */}
        {expanded && (
          <div className="px-3 pb-3 space-y-2" data-testid="audit-expanded">
            {item.request_payload && (
              <JsonViewer label="Request Payload" data={item.request_payload} />
            )}
            {item.response_payload && (
              <JsonViewer label="Response Payload" data={item.response_payload} />
            )}
            <div className="flex items-center gap-3 text-[9px] text-gray-400 font-mono pt-1">
              <span>ID: {item.id}</span>
              <span>Técnico: {item.technician_email || "—"} ({item.technician_id || "—"})</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function JsonViewer({ label, data }) {
  const [collapsed, setCollapsed] = useState(true);
  const jsonStr = typeof data === "string" ? data : JSON.stringify(data, null, 2);
  const preview = jsonStr.length > 80 ? jsonStr.slice(0, 80) + "..." : jsonStr;

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div
        className="flex items-center justify-between px-2.5 py-1.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setCollapsed(!collapsed)}
      >
        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">{label}</span>
        <span className="text-gray-400">
          {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
        </span>
      </div>
      {collapsed ? (
        <div className="px-2.5 py-1.5 text-[10px] font-mono text-gray-500 bg-gray-25 truncate">
          {preview}
        </div>
      ) : (
        <pre className="px-2.5 py-2 text-[10px] font-mono text-gray-700 bg-[#F8FAFC] overflow-x-auto max-h-[300px] overflow-y-auto whitespace-pre-wrap">
          {jsonStr}
        </pre>
      )}
    </div>
  );
}

function EndpointBadge({ endpoint, integrationName }) {
  const ec = ENDPOINT_COLORS[endpoint] || { bg: "#F3F4F6", fg: "#374151" };
  const label = integrationName || ENDPOINT_LABELS[endpoint] || endpoint;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
      style={{ background: ec.bg, color: ec.fg }}
      data-testid={`badge-endpoint-${endpoint}`}
    >
      {label}
    </span>
  );
}

// v97.3.0 · Evento V2 guardado sin enviar (modo manual / integración apagada): no es un fallo
function isPendingSync(errorMessage) {
  const m = String(errorMessage || "");
  return m.startsWith("Pendiente de envío") || m === "ODOO_WS_V2_ENABLED=false";
}

function StatusBadge({ success, httpStatus, errorMessage }) {
  if (!success && isPendingSync(errorMessage)) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">
        <Clock size={9} /> PENDIENTE
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${
        success ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
      }`}
    >
      {success ? <CheckCircle2 size={9} /> : <XCircle size={9} />}
      {httpStatus || (success ? "OK" : "FAIL")}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-EC", { timeZone: "America/Guayaquil", day: "2-digit", month: "short" }) +
      " " + d.toLocaleTimeString("es-EC", { timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  } catch {
    return iso;
  }
}
